import React, { useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FileDown } from 'lucide-react';
import { useRest, restFetch, RCard, RHead, RestLoading, SourceBadge, RestReconnect } from './restui';
import { DataVault } from './DataVault';

const KINDS = [
  ['toast', 'Toast (POS)'], ['doordash', 'DoorDash'], ['ubereats', 'Uber Eats'],
  ['grubhub', 'Grubhub'], ['direct', 'Direct / Website'], ['catering', 'Catering'], ['generic', 'Other CSV'],
];

export default function RestReports() {
  const { isEs, me } = useOutletContext();
  const { data: d, err, loading, reload } = useRest('/imports');
  const [kind, setKind] = useState('toast');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef();

  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', f); fd.append('kind', kind);
      const r = await restFetch('/imports/upload', { method: 'POST', body: fd });
      const j = await r.json();
      if (!r.ok) { setMsg({ ok: false, text: typeof j.detail === 'string' ? j.detail : 'Upload failed' }); return; }
      setMsg({ ok: true, text: `${j.rows_imported} ${isEs ? 'filas importadas' : 'rows imported'} (${j.period_from} → ${j.period_to})${j.rows_skipped ? ` · ${j.rows_skipped} ${isEs ? 'omitidas' : 'skipped'}` : ''}` });
      fileRef.current.value = '';
      reload();
    } catch (ex) { setMsg({ ok: false, text: ex.message }); } finally { setBusy(false); }
  };



  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await restFetch(`/report/monthly.pdf?lang=${isEs ? 'es' : 'en'}`);
      if (!r.ok) { setMsg({ ok: false, text: isEs ? 'Aún no hay datos suficientes para el informe.' : 'Not enough data for the report yet.' }); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kitxens-monthly-${me?.restaurant?.slug || 'report'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (ex) { setMsg({ ok: false, text: ex.message }); } finally { setPdfBusy(false); }
  };

  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;

  return (
    <div data-testid="rest-reports">
      <RHead title="Reports" sub={isEs ? 'Importa tus exports de Toast, DoorDash, Uber Eats, Grubhub o cualquier CSV de ventas.' : 'Import your Toast, DoorDash, Uber Eats, Grubhub exports or any sales CSV.'} />
      <RCard className="p-5 mb-4" testid="rest-monthly-report-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[14px] font-bold text-gray-900">{isEs ? 'Informe mensual de crecimiento (PDF)' : 'Monthly growth report (PDF)'}</p>
            <p className="mt-0.5 text-[12px] text-gray-500">{isEs ? 'El efecto Kitxens del mes, la proyección a 3 años y el desglose del retorno — listo para compartir con tu equipo o socios.' : 'This month\'s Kitxens effect, the 3-year projection and the return breakdown — ready to share with your team or partners.'}</p>
          </div>
          <button onClick={downloadPdf} disabled={pdfBusy} data-testid="rest-monthly-report-download"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[13px] px-5 py-2.5 transition-colors disabled:opacity-50">
            <FileDown className="w-4 h-4" />
            {pdfBusy ? (isEs ? 'Generando…' : 'Generating…') : (isEs ? 'Descargar PDF' : 'Download PDF')}
          </button>
        </div>
      </RCard>
      <RCard className="p-6" testid="rest-upload-card">
        <form onSubmit={upload} className="flex flex-wrap items-center gap-3">
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="rest-upload-kind"
            className="rounded-lg bg-[#FAFAF8] border border-gray-200 px-3 py-2 text-[13px] text-gray-600">
            {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input ref={fileRef} type="file" accept=".csv" required data-testid="rest-upload-file"
            className="text-[12.5px] text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-[12px] file:text-gray-900 file:cursor-pointer" />
          <button disabled={busy} data-testid="rest-upload-submit"
            className="rounded-lg bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[13px] px-5 py-2 transition-colors disabled:opacity-50">
            {busy ? (isEs ? 'Importando…' : 'Importing…') : (isEs ? 'Importar' : 'Import')}
          </button>
        </form>
        <p className="mt-3 text-[11.5px] text-gray-400">{isEs ? 'CSV con columnas de fecha y total (detectamos Toast/DoorDash/Uber Eats automáticamente). ¿Excel/PDF? Exporta a CSV primero.' : 'CSV with date and total columns (we auto-detect Toast/DoorDash/Uber Eats formats). Excel/PDF? Export to CSV first.'}</p>
        {msg && <p data-testid="rest-upload-result" className={`mt-3 text-[13px] font-semibold ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.ok ? '✓ ' : '✕ '}{msg.text}</p>}
      </RCard>

      <RCard className="mt-5 overflow-hidden" testid="rest-imports-list">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">📂 Data Vault</p>
          <SourceBadge source="manual_imports" isEs={isEs} />
        </div>
        <div className="px-5 py-3">
          <p className="text-[12px] text-gray-500">{isEs ? 'Cada archivo conserva su historial de versiones. Reemplazar o eliminar recalcula KPIs, Intelligence, Health Score y marca el Executive Brief para regenerarse — sin intervención manual.' : 'Every file keeps its version history. Replace or delete recalculates KPIs, Intelligence, Health Score and flags the Executive Brief for regeneration — no manual steps.'}</p>
        </div>
      </RCard>
      <DataVault isEs={isEs} isDemo={me?.role === 'demo'} />
    </div>
  );
}
